import * as React from 'react'
import { SxProps, sx as sxFunc } from '../sx'
import { StepIconProps, StepIcon } from '../StepIcon'
import {
  StepperContextType,
  useStepperContext,
} from '../Stepper/StepperContext'
import { StepContextType, useStepContext } from '../Step/StepContext'
import clsx from 'clsx'
import style from './index.module.scss'

export interface StepLabelClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `orientation="horizontal"`. */
  horizontal: string
  /** Styles applied to the root element if `orientation="vertical"`. */
  vertical: string
  /** Styles applied to the label element that wraps `children`. */
  label: string
  /** State class applied to the label element if `active={true}`. */
  active: string
  /** State class applied to the label element if `completed={true}`. */
  completed: string
  /** State class applied to the root and label elements if `error={true}`. */
  error: string
  /** State class applied to the root and label elements if `disabled={true}`. */
  disabled: string
  /** Styles applied to the `icon` container element. */
  iconcontainer: string
  /** State class applied to the root and icon container and label if `alternativeLabel={true}`. */
  alternativelabel: string
  /** Styles applied to the container element which wraps label and `optional`. */
  labelcontainer: string
}

export interface StepLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * In most cases will simply be a string containing a title for the label.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<StepLabelClasses>
  /**
   * If `true`, the step is marked as failed.
   * @default false
   */
  error?: boolean
  /**
   * Override the default label of the step icon.
   */
  icon?: React.ReactNode
  /**
   * The optional node to display.
   */
  optional?: React.ReactNode
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: {
    /**
     * Props applied to the label element.
     * @default {}
     */
    label?: React.HTMLProps<HTMLSpanElement>
  }
  /**
   * The component to render in place of the [`StepIcon`](/material-ui/api/step-icon/).
   */
  StepIconComponent?: React.ElementType
  /**
   * Props applied to the [`StepIcon`](/material-ui/api/step-icon/) element.
   */
  StepIconProps?: Partial<StepIconProps>
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}

export const StepLabel = React.forwardRef<HTMLSpanElement, StepLabelProps>(
  (props, ref) => {
    const {
      children,
      className,
      error = false,
      icon: iconProp,
      optional,
      slotProps = {},
      StepIconComponent: StepIconComponentProp,
      StepIconProps,
      classes,
      sx,
      ...other
    } = props

    const { alternativeLabel, orientation } =
      useStepperContext() as StepperContextType

    const {
      active,
      disabled,
      completed,
      icon: iconContext,
    } = useStepContext() as StepContextType
    const icon = iconProp || iconContext

    let StepIconComponent = StepIconComponentProp!

    if (icon && !StepIconComponent) {
      StepIconComponent = StepIcon
    }

    const labelSlotProps = slotProps.label
    return (
      //StepLabelRoot
      <span
        className={clsx(
          classes?.root,
          style.root,
          alternativeLabel && [
            style.alternativeLabel,
            classes?.alternativelabel,
          ],
          disabled && [style.disabled, classes?.disabled],
          classes?.[orientation],
          style?.[`orientation-${orientation}`],
          className,
          sxFunc(sx)
        )}
        ref={ref}
        {...other}
      >
        {icon || StepIconComponent ? (
          //StepLabelIconContainer
          <span
            className={clsx(
              classes?.iconcontainer,
              style.iconContainer,
              alternativeLabel && [
                style.alternativeLabel,
                classes?.alternativelabel,
              ]
            )}
          >
            <StepIconComponent
              completed={completed}
              active={active}
              error={error}
              icon={icon}
              {...StepIconProps}
            />
          </span>
        ) : null}
        {/* StepLabelLabelContainer */}
        <span
          className={clsx(
            classes?.labelcontainer,
            style.labelContainer,
            alternativeLabel && [
              style.alternativeLabel,
              classes?.alternativelabel,
            ]
          )}
        >
          {/* StepLabelLabel */}
          {children ? (
            <span
              className={clsx(
                classes?.label,
                style.label,
                alternativeLabel && [
                  style.alternativeLabel,
                  classes?.alternativelabel,
                ],
                completed && [style.completed, classes?.completed],
                error && [style.error, classes?.error],
                active && [style.active, classes?.active]
              )}
              {...labelSlotProps}
            >
              {children}
            </span>
          ) : null}
          {optional}
        </span>
      </span>
    )
  }
)
// @ts-ignore
StepLabel.factorName = 'StepLabel'

StepLabel.displayName = 'StepLabel'
